import React, { useState, useCallback } from 'react';
import { useTheme } from 'styled-components';
import Toaster from 'components/Toaster';
import {
  DataTable,
  SearchInput,
  AddButton,
  SegmentedFilter,
  ActionIcon,
  ConfirmModal,
  tokens,
} from 'components/v3';
import VideoModal from '../../LearningHub/components/VideoModal';
import VariantPill, { MODE_LABELS } from '../components/VariantPill';
import VideoFormModal from '../components/VideoFormModal';
import {
  useGetAdminVideos,
  useGetAdminTopics,
  useCreateVideo,
  useUpdateVideo,
  useDeleteVideo,
} from '../gql';
import { PageHeader, PageTitle, PageDescription } from '../StyledComponents';

const ITEMS_PER_PAGE = 15;

const COLUMNS = [
  {
    key: 'video',
    label: 'Video',
    width: 'minmax(280px, 2fr)',
    primary: true,
    skeleton: 'media',
  },
  {
    key: 'mode',
    label: 'Mode',
    width: 'minmax(100px, 150px)',
    skeleton: 'pill',
  },
  { key: 'topic', label: 'Topic', width: 'minmax(130px, 1fr)' },
  { key: 'duration', label: 'Duration', width: 'minmax(60px, 96px)' },
  { key: 'badges', label: 'Badges', width: 'minmax(80px, 170px)' },
  {
    key: 'status',
    label: 'Status',
    width: 'minmax(90px, 130px)',
    skeleton: 'pill',
  },
  {
    key: 'actions',
    label: 'Actions',
    width: '124px',
    align: 'right',
    skeleton: 'actions',
    actionsCount: 3,
  },
];

const MODE_FILTER_OPTIONS = [
  { key: '', label: 'All Modes' },
  { key: 'discover', label: 'Ideas & Tips' },
  { key: 'howto', label: 'How To Do It' },
];

const STATUS_FILTER_OPTIONS = [
  { key: '', label: 'All Statuses' },
  { key: 'published', label: 'Published' },
  { key: 'draft', label: 'Draft' },
];

const VideosPage = () => {
  const t = tokens(useTheme());
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState({ mode: '', status: '' });
  const [currentPage, setCurrentPage] = useState(1);
  const [showModal, setShowModal] = useState(false);
  const [editingVideo, setEditingVideo] = useState(null);
  const [previewVideo, setPreviewVideo] = useState(null);
  const [deletingVideo, setDeletingVideo] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [toaster, setToaster] = useState({ isOpen: false });

  const queryInput = {
    ...(search ? { search } : {}),
    ...(filters.mode ? { mode: filters.mode } : {}),
    ...(filters.status ? { status: filters.status } : {}),
    page: currentPage,
    limit: ITEMS_PER_PAGE,
  };

  const { videos, count, loading } = useGetAdminVideos(queryInput);
  const pageCount = Math.max(1, Math.ceil(count / ITEMS_PER_PAGE));
  const { topics } = useGetAdminTopics();
  const createVideo = useCreateVideo();
  const updateVideo = useUpdateVideo();
  const deleteVideo = useDeleteVideo();

  const handleSearch = useCallback((term) => {
    setSearch(term);
    setCurrentPage(1);
  }, []);

  const handleFilter = (key) => (value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  };

  const handleSave = async (data) => {
    const input = {
      topic_id: data.topic_id,
      mode: data.mode,
      title: data.title,
      description: data.description,
      vimeo_id: data.vimeo_id,
      duration_label: data.duration_label,
      status: data.status,
      is_new: data.is_new,
      is_featured: data.is_featured,
      help_link: data.help_link || null,
    };

    const isEditing = !!editingVideo;
    const result = isEditing
      ? await updateVideo({ id: editingVideo.id, ...input })
      : await createVideo(input);

    if (result) {
      setToaster({
        isOpen: true,
        type: 'success',
        title: isEditing ? 'Video Updated' : 'Video Created',
        message: `"${input.title}" has been ${
          isEditing ? 'updated' : 'added'
        }.`,
      });
      setShowModal(false);
      setEditingVideo(null);
    } else {
      setToaster({
        isOpen: true,
        type: 'error',
        title: 'Error',
        message: `Failed to ${
          isEditing ? 'update' : 'create'
        } video. Please try again.`,
      });
    }
  };

  const handleEdit = (video) => {
    setEditingVideo(video);
    setShowModal(true);
  };

  const handleAdd = () => {
    setEditingVideo(null);
    setShowModal(true);
  };

  const handleDeleteConfirm = async () => {
    setIsDeleting(true);
    const title = deletingVideo.title;
    const ok = await deleteVideo(deletingVideo.id);
    setIsDeleting(false);
    setDeletingVideo(null);
    setToaster(
      ok
        ? {
            isOpen: true,
            type: 'success',
            title: 'Video Deleted',
            message: `"${title}" has been deleted.`,
          }
        : {
            isOpen: true,
            type: 'error',
            title: 'Error',
            message: 'Failed to delete video. Please try again.',
          }
    );
  };

  const renderRow = (video) => ({
    video: (
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <img
          src={`https://vumbnail.com/${video.vimeo_id}.jpg`}
          alt=""
          style={{
            width: 80,
            height: 48,
            borderRadius: 6,
            objectFit: 'cover',
            flexShrink: 0,
          }}
          onError={(e) => {
            e.target.style.display = 'none';
          }}
        />
        <div style={{ minWidth: 0 }}>
          <div
            style={{
              fontWeight: 600,
              marginBottom: 4,
              color: t.text,
            }}
          >
            {video.title}
          </div>
          <div
            style={{
              fontSize: 14,
              color: t.sub,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {video.description}
          </div>
        </div>
      </div>
    ),
    mode: <VariantPill variant={video.mode} label={MODE_LABELS[video.mode]} />,
    topic: video.topic?.name || '',
    duration: video.duration_label,
    badges: (
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {video.is_new && <VariantPill variant="new" label="NEW" dot={false} />}
        {video.is_featured && (
          <VariantPill variant="featured" label="Featured" dot={false} />
        )}
      </div>
    ),
    status: (
      <VariantPill
        variant={video.status}
        label={video.status === 'published' ? 'Published' : 'Draft'}
      />
    ),
    actions: (
      <div style={{ display: 'flex', gap: 3 }}>
        <ActionIcon
          icon="eye"
          title="Preview"
          onClick={() => setPreviewVideo(video)}
        />
        <ActionIcon
          icon="square-pen"
          title="Edit"
          onClick={() => handleEdit(video)}
        />
        <ActionIcon
          icon="trash-2"
          title="Delete"
          onClick={() => setDeletingVideo(video)}
        />
      </div>
    ),
  });

  const previewVideoTransformed = previewVideo
    ? {
        id: String(previewVideo.id),
        title: previewVideo.title,
        description: previewVideo.description,
        duration: previewVideo.duration_label,
        vimeoId: previewVideo.vimeo_id,
        isNew: previewVideo.is_new,
        helpLink: previewVideo.help_link,
      }
    : null;

  return (
    <>
      <PageHeader>
        <div>
          <PageTitle>Videos</PageTitle>
          <PageDescription>Manage learning hub video content</PageDescription>
        </div>
      </PageHeader>

      <DataTable
        columns={COLUMNS}
        data={videos}
        renderRow={renderRow}
        loading={loading}
        emptyLabel="No videos found."
        page={currentPage}
        pageCount={pageCount}
        onPageChange={setCurrentPage}
        toolbar={
          <>
            <SearchInput
              onChange={handleSearch}
              placeholder="Search videos..."
              debounce={400}
            />
            <AddButton title="Add Video" onClick={handleAdd} />
          </>
        }
        filter={
          <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
            <SegmentedFilter
              value={filters.mode}
              onChange={handleFilter('mode')}
              options={MODE_FILTER_OPTIONS}
            />
            <SegmentedFilter
              value={filters.status}
              onChange={handleFilter('status')}
              options={STATUS_FILTER_OPTIONS}
            />
          </div>
        }
      />

      {showModal && (
        <VideoFormModal
          video={editingVideo}
          topics={topics}
          onSave={handleSave}
          onClose={() => {
            setShowModal(false);
            setEditingVideo(null);
          }}
        />
      )}

      <VideoModal
        video={previewVideoTransformed}
        onClose={() => setPreviewVideo(null)}
        mode={previewVideo?.mode}
      />

      <ConfirmModal
        open={!!deletingVideo}
        title="Delete Video?"
        message={`Are you sure you want to delete ${deletingVideo?.title}? This action cannot be undone.`}
        loading={isDeleting}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeletingVideo(null)}
      />

      <Toaster
        isOpen={toaster.isOpen}
        onClose={() => setToaster({ isOpen: false })}
        type={toaster.type}
        title={toaster.title}
        message={toaster.message}
      />
    </>
  );
};

export default VideosPage;
